import type { MessageFormatElement } from '@formatjs/icu-messageformat-parser';
import { Effect, Match } from 'effect';
import { createIntl, createIntlCache } from 'react-intl/server';

import { getDefaultLocale, Locale, type LocaleT } from '../config/locale';

import csMessages from '../i18n/locales/cs.json';
import skMessages from '../i18n/locales/sk.json';
import csMessagesCompiled from '../i18n/locales-compiled/cs.json';
import skMessagesCompiled from '../i18n/locales-compiled/sk.json';

type Messages = Record<string, string> | Record<string, MessageFormatElement[]>;

const getMessagesCompiledByLocale = (locale: LocaleT): Messages =>
  Match.value(locale).pipe(
    Match.when(Locale.CS, () => csMessagesCompiled),
    Match.when(Locale.SK, () => skMessagesCompiled),
    Match.exhaustive
  );

const getMessagesNonCompiledByLocale = (locale: LocaleT): Messages =>
  Match.value(locale).pipe(
    Match.when(Locale.CS, () => csMessages),
    Match.when(Locale.SK, () => skMessages),
    Match.exhaustive
  );

const getMessagesByLocale = (locale: LocaleT, compiled: boolean): Messages =>
  compiled ? getMessagesCompiledByLocale(locale) : getMessagesNonCompiledByLocale(locale);

const cache = createIntlCache();

function createIntlShape(locale: LocaleT, compiled = false) {
  const messages = getMessagesByLocale(locale, compiled);

  return createIntl(
    {
      locale: locale,
      messages: messages,
    },
    cache
  );
}

export default createIntlShape;

const intlShapeCache = new Map<string, ReturnType<typeof createIntl>>();

export const getMemoizedIntlShape = (
  locale: LocaleT,
  compiled: boolean
): ReturnType<typeof createIntl> => {
  const cacheKey = `${locale}:${compiled ? 'compiled' : 'dev'}`;

  const cached = intlShapeCache.get(cacheKey);
  if (cached) {
    return cached;
  }

  const intlShape = createIntlShape(locale, compiled);
  intlShapeCache.set(cacheKey, intlShape);

  return intlShape;
};

/**
 * Production memoizes the shape built from the compiled catalog. Development builds an
 * unmemoized shape from the source catalog, which has no compile step.
 */
export const getIntlShapeForLocale = (locale: LocaleT): ReturnType<typeof createIntl> =>
  process.env.NODE_ENV === 'production'
    ? getMemoizedIntlShape(locale, true)
    : createIntlShape(locale, false);

export const getMemoizedIntlShapeWithDefaultLocale = () =>
  getIntlShapeForLocale(getDefaultLocale());

// v4: `Effect.cachedFunction` was removed. Use the existing module-level
// `intlShapeCache` Map (`getMemoizedIntlShape`) for the by-locale memoization
// it provided.
export const createIntlForLocaleCachedEffect = (locale: LocaleT) =>
  Effect.sync(() => getMemoizedIntlShape(locale, true));
